/*
 * Don Juan Pomade — Phase 2 V12 mobile slideshow image scheduling.
 * Scope: only the homepage <source data-dj-v4-srcset> markers from _slide.liquid.
 * Original Ritual carousel, gestures, accessibility, media and slide transitions
 * are untouched. Progressive preload limits early network competition.
 */
(() => {
  'use strict';
  const media = window.matchMedia('(max-width: 749px)');
  const selector = 'slideshow-component';
  const timers = new WeakMap();
  // Prewarm exactly one upcoming hero image only after the initial hero loads.
  // Never changes Ritual's native carousel interval or event handling.
  const firstNextWarmed = new WeakSet();
  const warmingRequests = new WeakMap();

  function restore(slide) {
    if (!slide) return;
    slide.querySelectorAll('source[data-dj-v4-srcset]').forEach((source) => {
      const srcset = source.getAttribute('data-dj-v4-srcset');
      if (!srcset) return;
      source.setAttribute('srcset', srcset);
      source.removeAttribute('data-dj-v4-srcset');
    });
  }

  function slidesOf(carousel) {
    const track = carousel.querySelector('slideshow-container > slideshow-slides');
    return track ? Array.from(track.children).filter((child) => child.localName === 'slideshow-slide') : [];
  }

  function currentIndex(carousel) {
    const slides = slidesOf(carousel);
    const current = slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false');
    if (current >= 0) return current;
    const initial = Number.parseInt(carousel.getAttribute('initial-slide') || '0', 10);
    return Number.isFinite(initial) ? initial : 0;
  }

  function warm(carousel, index, neighbours = false) {
    if (!media.matches) return;
    const slides = slidesOf(carousel);
    if (!slides.length) return;
    const i = ((index % slides.length) + slides.length) % slides.length;
    restore(slides[i]);
    if (neighbours && slides.length > 1) {
      restore(slides[(i + 1) % slides.length]);
      restore(slides[(i + slides.length - 1) % slides.length]);
    }
  }

  function warmHeroSecondOnce(carousel) {
    if (firstNextWarmed.has(carousel) || !media.matches || document.hidden) return;
    // Never prefetch sections further down the page or non-hero carousels.
    if (carousel !== document.querySelector('main slideshow-component')) return;
    if (!carousel.hasAttribute('autoplay') || currentIndex(carousel) !== 0) return;
    if (navigator.connection?.saveData || /^(slow-2g|2g)$/.test(navigator.connection?.effectiveType || '')) return;

    const slides = slidesOf(carousel);
    if (slides.length < 2) return;
    const first = slides[0].querySelector('picture.slide-image-responsive > img.slide__image');
    const second = slides[1].querySelector('source[data-dj-v4-srcset]');
    const srcset = second?.getAttribute('data-dj-v4-srcset');
    if (!first || !srcset) return;

    // The first visible banner must finish loading before we spend bandwidth
    // on a subsequent autoplay image. No modal, transition or click is changed.
    const afterFirstLoaded = () => {
      if (!carousel.isConnected || !media.matches || document.hidden || currentIndex(carousel) !== 0) return;
      if (firstNextWarmed.has(carousel)) return;
      firstNextWarmed.add(carousel);
      const request = new Image();
      request.decoding = 'async';
      // V12: once the first hero has loaded, normal priority gives the next
      // slide a better chance to be ready before the existing 4s autoplay.
      // This does not change the autoplay clock or first hero's priority.
      request.fetchPriority = 'auto';
      request.sizes = '100vw';
      request.srcset = srcset;
      // The fallback URL is used only by browsers without srcset support.
      request.src = srcset.trim().split(',')[0].trim().split(/\s+/)[0];
      warmingRequests.set(carousel, request);
      const release = () => warmingRequests.delete(carousel);
      request.addEventListener('load', release, { once: true });
      request.addEventListener('error', release, { once: true });
    };

    if (first.complete && first.naturalWidth > 0) afterFirstLoaded();
    else first.addEventListener('load', afterFirstLoaded, { once: true });
  }

  function cancelPrefetch(carousel) {
    const timer = timers.get(carousel);
    if (timer !== undefined) clearTimeout(timer);
    timers.delete(carousel);
  }

  function scheduleAutoplayNext(carousel, active) {
    cancelPrefetch(carousel);
    if (!media.matches || document.hidden || carousel.hasAttribute('paused')) return;
    const seconds = Number.parseInt(carousel.getAttribute('autoplay') || '', 10);
    if (!Number.isFinite(seconds) || seconds < 3) return;
    // The current image has priority. Warm only the next image shortly before
    // the theme's autoplay interval, not immediately on first page render.
    const timer = setTimeout(() => {
      timers.delete(carousel);
      if (!carousel.isConnected || !media.matches || document.hidden || carousel.hasAttribute('paused')) return;
      warm(carousel, active + 1);
    }, Math.max(750, seconds * 1000 - 1600));
    timers.set(carousel, timer);
  }

  function activate(carousel) {
    const active = currentIndex(carousel);
    warm(carousel, active);
    warmHeroSecondOnce(carousel);
    scheduleAutoplayNext(carousel, active);
  }

  const observer = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || !media.matches) return;
          activate(entry.target);
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '350px 0px' })
    : null;

  function setup(carousel) {
    if (carousel.dataset.djV4Ready) return;
    if (!carousel.querySelector('source[data-dj-v4-srcset]')) return;
    carousel.dataset.djV4Ready = 'true';

    carousel.addEventListener('slideshow:select', (event) => {
      if (event.target !== carousel || !media.matches) return;
      const index = event.detail && event.detail.index;
      if (!Number.isInteger(index)) return;
      warm(carousel, index);
      scheduleAutoplayNext(carousel, index);
    });

    // V7: only an actual slide interaction or carousel navigation control
    // needs both neighboring images. Avoid large image requests when a user
    // taps a CTA/button inside a slide (especially on slow mobile networks).
    // Gestures, arrows, dots and keyboard carousel controls still prewarm.
    const navigationTarget = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return false;
      if (target.closest('slideshow-arrows, slideshow-controls')) return true;
      if (!target.closest('slideshow-slides')) return false;
      return !target.closest('a[href], button, input, select, textarea, [role="button"]');
    };
    const warmAdjacent = (event) => {
      if (navigationTarget(event)) warm(carousel, currentIndex(carousel), true);
    };
    carousel.addEventListener('pointerdown', warmAdjacent, { passive: true });
    carousel.addEventListener('focusin', warmAdjacent);

    if (observer) observer.observe(carousel);
    else slidesOf(carousel).forEach(restore); // Fail open for older browsers.
  }

  function scan() {
    document.querySelectorAll(selector).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll(`${selector}[data-dj-v4-ready]`).forEach((carousel) => {
      if (document.hidden) cancelPrefetch(carousel);
      else if (media.matches) activate(carousel);
    });
  });
  if (typeof media.addEventListener === 'function') {
    media.addEventListener('change', (event) => {
      if (!event.matches) {
        document.querySelectorAll(`${selector}[data-dj-v4-ready]`).forEach(cancelPrefetch);
        return;
      }
      scan();
      document.querySelectorAll(`${selector}[data-dj-v4-ready]`).forEach(activate);
    });
  }
})();
